import React, { useState, useEffect } from "react";
import { Col, Form, Input, Row, Button,Select, Typography } from "antd";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import axios from "axios";
import toast from "react-hot-toast";
import { showLoading, hideLoading } from "../../redux/alertsSlice";
import Layout from "../../components/layout";
import {
  UserOutlined, MailOutlined, PhoneOutlined, EnvironmentOutlined,
  LockOutlined, TeamOutlined, UserAddOutlined, SafetyOutlined,
  MedicineBoxOutlined, SettingOutlined
} from "@ant-design/icons";

const { Option } = Select;
const { Title, Text } = Typography;

/* ─── Tokens ─────────────────────────────────────────────────── */
const EMERALD    = "#0d9488";
const TEAL       = "#14b8a6";
const TEAL_LIGHT = "#ccfbf1";
const SLATE      = "#0f172a";
const SLATE_MID  = "#334155";
const MUTED      = "#94a3b8";
const SURFACE    = "#f8fafc";
const WHITE      = "#ffffff";

/* ─── Style injection ─────────────────────────────────────────── */
const injectStyles = () => {
  if (document.getElementById("staff-reg-styles")) return;
  const el = document.createElement("style");
  el.id = "staff-reg-styles";
  el.textContent = `
    @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;600;700;800&family=DM+Sans:ital,wght@0,300;0,400;0,500;1,300&display=swap');

    .sr-root { font-family: 'DM Sans', sans-serif; background: ${SURFACE}; min-height: 100vh; }
    .sr-wrap { max-width: 700px; margin: 0 auto; padding: 32px 24px 64px; }

    /* Hero */
    .sr-hero {
      background: linear-gradient(135deg, ${SLATE} 0%, ${SLATE_MID} 100%);
      border-radius: 24px; padding: 36px 44px;
      position: relative; overflow: hidden; margin-bottom: 32px;
    }
    .sr-hero::before {
      content: ''; position: absolute; top: -80px; right: -80px;
      width: 300px; height: 300px; border-radius: 50%;
      background: radial-gradient(circle, ${TEAL}44 0%, transparent 70%);
      filter: blur(32px); pointer-events: none;
    }
    .sr-hero::after {
      content: ''; position: absolute; bottom: -60px; left: -40px;
      width: 200px; height: 200px; border-radius: 50%;
      background: radial-gradient(circle, ${EMERALD}33 0%, transparent 70%);
      filter: blur(26px); pointer-events: none;
    }
    .sr-eyebrow {
      display: inline-block; padding: 4px 12px; border-radius: 20px;
      background: ${TEAL_LIGHT}; color: ${EMERALD};
      font-size: 11px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase;
      font-family: 'Sora', sans-serif; margin-bottom: 12px;
    }

    /* Card section */
    .sr-section {
      background: ${WHITE}; border-radius: 20px;
      box-shadow: 0 4px 20px rgba(0,0,0,.06);
      padding: 28px 28px 20px; margin-bottom: 20px;
    }
    .sr-section-head {
      display: flex; align-items: center; gap: 10px; margin-bottom: 24px;
      padding-bottom: 16px; border-bottom: 1.5px solid #f1f5f9;
    }
    .sr-section-icon {
      width: 36px; height: 36px; border-radius: 10px; flex-shrink: 0;
      background: ${TEAL_LIGHT}; color: ${EMERALD};
      display: flex; align-items: center; justify-content: center; font-size: 16px;
    }

    /* Form spacing */
    .sr-root .ant-form-item { margin-bottom: 20px !important; }
    .sr-root .ant-form-item-label { padding-bottom: 4px !important; line-height: 1 !important; }
    .sr-root .ant-form-item-label > label {
      font-size: 12px !important; font-weight: 600 !important;
      color: ${SLATE_MID} !important; letter-spacing: .2px;
      height: 20px !important; line-height: 20px !important;
    }
    .sr-root .ant-form-item-explain { min-height: 0 !important; font-size: 11px !important; }

    /* Plain input */
    .sr-root .ant-input {
      border-radius: 10px !important; height: 44px !important; line-height: 44px !important;
      border: 1.5px solid #e2e8f0 !important; padding: 0 12px !important;
      font-family: 'DM Sans', sans-serif !important; font-size: 13px !important;
      box-sizing: border-box !important;
      transition: border-color .2s, box-shadow .2s !important;
    }
    .sr-root .ant-input:hover, .sr-root .ant-input:focus {
      border-color: ${TEAL} !important; box-shadow: 0 0 0 3px ${TEAL}22 !important;
    }

    /* Input with prefix/suffix */
    .sr-root .ant-input-affix-wrapper {
      border-radius: 10px !important; height: 44px !important;
      border: 1.5px solid #e2e8f0 !important; padding: 0 12px !important;
      display: flex !important; align-items: center !important;
      font-family: 'DM Sans', sans-serif !important; font-size: 13px !important;
      box-sizing: border-box !important;
      transition: border-color .2s, box-shadow .2s !important;
    }
    .sr-root .ant-input-affix-wrapper .ant-input {
      height: 100% !important; border: none !important; box-shadow: none !important;
      padding: 0 !important; background: transparent !important;
    }
    .sr-root .ant-input-affix-wrapper:hover,
    .sr-root .ant-input-affix-wrapper-focused {
      border-color: ${TEAL} !important; box-shadow: 0 0 0 3px ${TEAL}22 !important;
    }

    /* Password input eye icon fix */
    .sr-root .ant-input-password { padding-right: 12px !important; }

    /* Select */
    .sr-root .ant-select-selector {
      border-radius: 10px !important; height: 44px !important;
      border: 1.5px solid #e2e8f0 !important; padding: 0 12px !important;
      display: flex !important; align-items: center !important;
      font-family: 'DM Sans', sans-serif !important; font-size: 13px !important;
      box-sizing: border-box !important;
      transition: border-color .2s, box-shadow .2s !important;
    }
    .sr-root .ant-select-selection-item,
    .sr-root .ant-select-selection-placeholder { line-height: 42px !important; }
    .sr-root .ant-select-focused .ant-select-selector,
    .sr-root .ant-select-selector:hover {
      border-color: ${TEAL} !important; box-shadow: 0 0 0 3px ${TEAL}22 !important;
    }

    /* Role selector cards */
    .sr-role-group { display: flex; gap: 12px; flex-wrap: wrap; }
    .sr-role-card {
      flex: 1; min-width: 120px;
      border: 1.5px solid #e2e8f0; border-radius: 12px;
      padding: 14px 16px; cursor: pointer;
      display: flex; align-items: center; gap: 10px;
      transition: border-color .2s, background .2s;
      background: ${WHITE};
    }
    .sr-role-card:hover { border-color: ${TEAL}; background: #f0fdfb; }
    .sr-role-card.selected { border-color: ${TEAL}; background: #f0fdfb; }
    .sr-role-card.selected .sr-role-icon { background: ${TEAL}; color: ${WHITE}; }
    .sr-role-icon {
      width: 32px; height: 32px; border-radius: 8px; flex-shrink: 0;
      background: ${SURFACE}; color: ${MUTED};
      display: flex; align-items: center; justify-content: center;
      font-size: 14px; transition: background .2s, color .2s;
    }
    .sr-role-label {
      font-size: 13px; font-weight: 600; color: ${SLATE_MID};
      font-family: 'DM Sans', sans-serif;
    }
    .sr-role-card.selected .sr-role-label { color: ${EMERALD}; }

    /* Hint box */
    .sr-hint {
      background: ${TEAL_LIGHT}; border-radius: 12px; padding: 12px 16px;
      display: flex; align-items: flex-start; gap: 10px; margin-bottom: 24px;
    }
    .sr-hint-dot {
      width: 6px; height: 6px; border-radius: 50%;
      background: ${EMERALD}; flex-shrink: 0; margin-top: 5px;
    }

    /* Submit */
    .sr-submit {
      height: 52px !important; width: 100% !important;
      border-radius: 14px !important;
      font-size: 15px !important; font-weight: 700 !important;
      font-family: 'Sora', sans-serif !important;
      background: linear-gradient(135deg, ${EMERALD}, ${TEAL}) !important;
      border: none !important; color: ${WHITE} !important;
      box-shadow: 0 8px 20px ${TEAL}44 !important;
      transition: transform .2s, box-shadow .2s !important;
    }
    .sr-submit:hover {
      transform: translateY(-2px) !important;
      box-shadow: 0 14px 32px ${TEAL}55 !important;
    }

    /* Animations */
    @keyframes fadeUp {
      from { opacity: 0; transform: translateY(16px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    .sr-fade   { animation: fadeUp .5s ease both; }
    .sr-fade-1 { animation-delay: .05s; }
    .sr-fade-2 { animation-delay: .12s; }
    .sr-fade-3 { animation-delay: .20s; }
    .sr-fade-4 { animation-delay: .28s; }
  `;
  document.head.appendChild(el);
};

/* ─── Role option data ────────────────────────────────────────── */
const roles = [
  { value: "staff",  label: "Staff",  icon: <TeamOutlined /> },
  { value: "doctor", label: "Doctor", icon: <MedicineBoxOutlined /> },
  { value: "admin",  label: "Admin",  icon: <SettingOutlined /> },
];

/* ─── Section wrapper ─────────────────────────────────────────── */
function Section({ icon, title, children, className = "" }) {
  return (
    <div className={`sr-section ${className}`}>
      <div className="sr-section-head">
        <div className="sr-section-icon">{icon}</div>
        <Title level={5} style={{ margin: 0, fontFamily: "'Sora', sans-serif", color: SLATE, fontWeight: 700 }}>
          {title}
        </Title>
      </div>
      {children}
    </div>
  );
}

/* ─── Main component ──────────────────────────────────────────── */
function StaffRegister() {
  const [role, setRole]           = useState("staff");
  const [locations, setLocations] = useState([]);
  const dispatch  = useDispatch();
  const navigate  = useNavigate();

  useEffect(() => { injectStyles(); }, []);

  useEffect(() => {
    axios.get("/api/user/get-all-location", {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    })
      .then(res => { if (res.data.success) setLocations(res.data.data); })
      .catch(console.error);
  }, []);

  const onFinish = async (values) => {
    try {
      dispatch(showLoading());
      const res = await axios.post("/api/admin/register", {
        ...values,
        role,
        isStaff:   role === "staff",
        isAdmin:   role === "admin",
        regDoctor: role === "doctor",
      });
      dispatch(hideLoading());
      if (res.data.success) {
        toast.success(res.data.message);
        navigate("/");
      } else {
        toast.error(res.data.message);
      }
    } catch {
      dispatch(hideLoading());
      toast.error("Something went wrong");
    }
  };

  return (
    <Layout>
      <div className="sr-root">
        <div className="sr-wrap">

          {/* Hero */}
          <div className="sr-hero sr-fade">
            <div style={{ position: "relative", zIndex: 1 }}>
              <div className="sr-eyebrow">
                <UserAddOutlined style={{ marginRight: 5 }} />User Management
              </div>
              <Title level={2} style={{ margin: "6px 0 4px", fontFamily: "'Sora', sans-serif", color: WHITE, fontWeight: 800 }}>
                Staff Registration
              </Title>
              <Text style={{ color: MUTED, fontSize: 14 }}>
                Create a new staff, doctor, or admin account and assign their role
              </Text>
            </div>
          </div>

          <Form layout="vertical" onFinish={onFinish} requiredMark={false}>

            {/* Section 1 — Personal Info */}
            <Section icon={<UserOutlined />} title="Personal Information" className="sr-fade sr-fade-1">
              <Row gutter={[20, 0]}>
                <Col xs={24}>
                  <Form.Item label="Full Name" name="name" rules={[{ required: true, message: "Please enter full name!" }]}>
                    <Input placeholder="Enter full name" prefix={<UserOutlined style={{ color: MUTED }} />} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item label="Email Address" name="email" rules={[{ required: true, message: "Please enter email!" }]}>
                    <Input placeholder="Enter email address" prefix={<MailOutlined style={{ color: MUTED }} />} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item label="Phone Number" name="phone" rules={[{ required: true, message: "Please enter phone number!" }]}>
                    <Input placeholder="Enter phone number" prefix={<PhoneOutlined style={{ color: MUTED }} />} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item label="Location" name="location" rules={[{ required: true, message: "Please select a location!" }]}>
                    <Select placeholder="Select a location" suffixIcon={<EnvironmentOutlined style={{ color: MUTED }} />}>
                      {locations.map(l => (
                        <Option key={l._id} value={l._id}>{l.location}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item label="Passcode" name="password" rules={[{ required: true, message: "Please enter a passcode!" }]}>
                    <Input.Password placeholder="Enter passcode" prefix={<LockOutlined style={{ color: MUTED }} />} />
                  </Form.Item>
                </Col>
              </Row>
            </Section>

            {/* Section 2 — Role */}
            <Section icon={<SafetyOutlined />} title="Assign Role" className="sr-fade sr-fade-2">
              <div className="sr-hint">
                <div className="sr-hint-dot" />
                <Text style={{ color: EMERALD, fontSize: 12, lineHeight: 1.5 }}>
                  Select the role that best matches this user's responsibilities. Role determines access permissions within the system.
                </Text>
              </div>

              <div className="sr-role-group">
                {roles.map(r => (
                  <div
                    key={r.value}
                    className={`sr-role-card ${role === r.value ? "selected" : ""}`}
                    onClick={() => setRole(r.value)}
                  >
                    <div className="sr-role-icon">{r.icon}</div>
                    <span className="sr-role-label">{r.label}</span>
                  </div>
                ))}
              </div>
            </Section>

            {/* Submit */}
            <div className="sr-fade sr-fade-3">
              <Button className="sr-submit" htmlType="submit" icon={<UserAddOutlined />}>
                Create Account
              </Button>
            </div>

          </Form>
        </div>
      </div>
    </Layout>
  );
}

export default StaffRegister;